class StorefrontClient {
  constructor() {
    this.endpoint = `https://brain-effect.com/api/2026-10/graphql.json`;

    const CART_FIELDS_FRAGMENT = `
      appliedGiftCards {
        lastCharacters
        presentmentAmountUsed { amount currencyCode }
      }
      buyerIdentity {
        purchasingCompany {
          company {
            id
          }
        }
      }
      cost {
        subtotalAmount { amount currencyCode }
        totalAmount { amount currencyCode }
      }
      discountCodes {
        applicable
        code
      }
      freeShipping: attribute(key: "_free_shipping") { value }
      id
      lines(first: 50) {
        edges {
          node {
            id
            quantity
            cost {
              subtotalAmount { amount currencyCode }
              totalAmount { amount currencyCode }
            }
            discountAllocations {
              discountedAmount { amount currencyCode }
            }
            merchandise {
              ... on ProductVariant {
                id
                title
                availableForSale
                image { url }
                price { amount currencyCode }
                compareAtPrice { amount currencyCode }
                product {
                  id
                  handle
                  title
                  isGiftCard
                  featuredImage { url }
                }
              }
            }
            sellingPlanAllocation {
              sellingPlan {
                id
                recurringDeliveries
              }
            }
          }
        }
      }
    `;

    this.cartQuery = `
      query getCart($cartId: ID!, $language: LanguageCode) @inContext(language: $language) {
        cart(id: $cartId) {
          ${CART_FIELDS_FRAGMENT}
        }
      }
    `;

    this.giftCardMutation = `
      mutation cartGiftCardCodesUpdate($cartId: ID!, $giftCardCodes: [String!]!) {
        cartGiftCardCodesUpdate(cartId: $cartId, giftCardCodes: $giftCardCodes) {
          cart {
            appliedGiftCards {
              lastCharacters
              presentmentAmountUsed { amount currencyCode }
            }
            cost {
              subtotalAmount { amount currencyCode }
              totalAmount { amount currencyCode }
            }
          }
          userErrors { field message }
          warnings { code message }
        }
      }
    `;

    this.variantLookupQuery = `
      query giftPickerVariants($ids: [ID!]!, $language: LanguageCode) @inContext(language: $language) {
        nodes(ids: $ids) {
          ... on ProductVariant {
            id
            title
            availableForSale
            image { url }
            price { amount }
            compareAtPrice { amount }
            product {
              handle
              title
              featuredImage { url }
            }
          }
        }
      }
    `;

    this.bundleComponentsBbdQuery = `
      query bundleComponentsBbd($id: ID!, $language: LanguageCode) @inContext(language: $language) {
        node(id: $id) {
          ... on ProductVariant {
            components(first: 50) {
              nodes {
                productVariant {
                  id
                  title
                  product { title }
                  shortBbd: metafield(namespace: "custom", key: "short_bbd") { value }
                  extendedBbd: metafield(namespace: "custom", key: "extended_bbd") { value }
                }
              }
            }
          }
        }
      }
    `;

    this.discountCodeMutation = `
      mutation cartDiscountCodesUpdate($cartId: ID!, $discountCodes: [String!]!) {
        cartDiscountCodesUpdate(cartId: $cartId, discountCodes: $discountCodes) {
          cart {
            ${CART_FIELDS_FRAGMENT}
          }
          userErrors {
            code
            field
            message
          }
          warnings {
            code
            message
            target
          }
        }
      }
    `;
  }

  _getLanguageCode() {
    return ((window.Shopify && window.Shopify.locale) || 'de').toUpperCase();
  }

  async _fetchGraphQL(query, variables, {useToken = false} = {}) {
    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    };
    if (useToken) {
      // (headless) public access token - MUST NOT be sent on cart mutations.
      headers['X-Shopify-Storefront-Access-Token'] = '0b0876728c355b17ecd42d80aa618f41';
    }
    const res = await fetch(this.endpoint, {
      method: 'POST',
      headers,
      body: JSON.stringify({query, variables})
    });
    const json = await res.json();
    if (json.errors) {
      console.error('Storefront API errors: ', json.errors);
      return;
    }
    return json.data;
  }

  async _getCartId() {
    const cart = await (await fetch(window.Shopify.routes.root + 'cart.js', {
      method: 'GET',
      headers: {"Accept": "application/json", "Content-Type": "application/json"}
    })).json();
    if (!cart.token) return;
    return `gid://shopify/Cart/${cart.token}`;
  }

  async applyDiscountCode(discountCode) {
    const cartId = await this._getCartId();
    if (!cartId) return;
    return await this._fetchGraphQL(this.discountCodeMutation, {cartId, discountCodes: [discountCode]});
  }

  async getCartInfo() {
    const cartId = await this._getCartId();
    if (!cartId) return;
    return await this._fetchGraphQL(this.cartQuery, {cartId, language: this._getLanguageCode()});
  }

  async applyGiftCard(code) {
    const giftCardCodes = code ? [code.trim()] : [];
    const cartId = await this._getCartId();
    if (!cartId) return;

    const data = await this._fetchGraphQL(this.giftCardMutation, {cartId, giftCardCodes});

    return {
      success: !!data?.cartGiftCardCodesUpdate?.cart,
      userErrors: data?.cartGiftCardCodesUpdate?.userErrors || [],
      warnings: data?.cartGiftCardCodesUpdate?.warnings || [],
    };
  }

  _normalizeVariantId(candidate) {
    if (typeof candidate === 'number' && Number.isFinite(candidate)) {
      return `${Math.trunc(candidate)}`;
    }
    if (typeof candidate === 'string') {
      const normalized = candidate.trim();
      if (!normalized) return '';
      if (/^\d+$/.test(normalized)) return normalized;
      const tail = normalized.split('/').pop();
      if (tail && /^\d+$/.test(tail)) return tail;
      const match = normalized.match(/(\d+)(?!.*\d)/);
      if (match) return match[1];
    }
    return '';
  }

  _toVariantGid(id) {
    if (typeof id === 'string' && id.startsWith('gid://')) return id;
    const normalized = this._normalizeVariantId(id);
    if (!normalized) return '';
    return `gid://shopify/ProductVariant/${normalized}`;
  }

  async fetchVariantsByIds(ids) {
    const gids = [];
    (Array.isArray(ids) ? ids : []).forEach((id) => {
      const gid = this._toVariantGid(id);
      if (gid) gids.push(gid);
    });
    if (!gids.length) return [];

    const unique = Array.from(new Set(gids));
    const chunkSize = 25;
    const nodes = [];

    for (let i = 0; i < unique.length; i += chunkSize) {
      const chunk = unique.slice(i, i + chunkSize);
      try {
        const response = await this._fetchGraphQL(this.variantLookupQuery, {ids: chunk, language: this._getLanguageCode()});
        const chunkNodes = response?.nodes;
        if (Array.isArray(chunkNodes)) {
          chunkNodes.forEach((node) => {
            if (node) {
              nodes.push(node);
            }
          });
        }
      } catch (error) {}
    }

    return nodes;
  }

  async fetchBundleComponentsBbd(variantId) {
    const id = this._toVariantGid(variantId);
    if (!id) return [];
    let data;
    try {
      data = await this._fetchGraphQL(this.bundleComponentsBbdQuery, {id, language: this._getLanguageCode()}, {useToken: true});
    } catch (error) {
      return [];
    }
    const nodes = data?.node?.components?.nodes;
    if (!Array.isArray(nodes)) return [];
    const results = [];
    nodes.forEach((node) => {
      const cv = node?.productVariant;
      if (!cv) return;
      const shortBbd = cv.shortBbd?.value || '';
      const extendedBbd = cv.extendedBbd?.value || '';
      if (!shortBbd && !extendedBbd) return;
      const productTitle = cv.product?.title || '';
      const variantTitle = cv.title || '';
      const name = (!variantTitle || variantTitle === 'Default Title')
        ? productTitle
        : `${productTitle} - ${variantTitle}`;
      results.push({name, shortBbd, extendedBbd});
    });
    return results;
  }
}
window.FoxTheme = window.FoxTheme || {};
window.FoxTheme.storefrontClient = new StorefrontClient();
